import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  BellRing,
  Eye,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Radio,
  Settings,
  ShieldCheck,
  Sun,
  Swords,
  User,
  Users,
  X,
} from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { logout } from "@/lib/auth";
import { useTheme } from "@/hooks/useTheme";
import { setTheme } from "@/lib/theme";
import { useAdminFaction } from "@/hooks/useAdminFaction";
import { clearAdminFactionOverride } from "@/lib/adminFaction";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true, the link is hidden unless the user passes the gate. */
  gate?: (auth: ReturnType<typeof useAuth>) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/war", label: "War", icon: Radio },
  { to: "/war-reports", label: "War Reports", icon: Swords },
  { to: "/me", label: "My Contributions", icon: User },
  {
    to: "/members",
    label: "Members",
    icon: Users,
    gate: (a) => a.isLeader || a.isServiceAdmin,
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    gate: (a) => a.isLeader || a.isServiceAdmin,
  },
  {
    to: "/nags",
    label: "Nags",
    icon: BellRing,
    gate: (a) => a.isLeader || a.isServiceAdmin,
  },
  {
    to: "/admin",
    label: "Admin",
    icon: ShieldCheck,
    gate: (a) => a.isServiceAdmin,
  },
  {
    to: "/admin/stats",
    label: "Travel Stats",
    icon: BarChart3,
    gate: (a) => a.isServiceAdmin,
  },
];

/**
 * Sidebar contents — header, nav, footer. Rendered inside the static
 * desktop sidebar and inside the mobile slide-in drawer so both
 * surfaces stay in sync.
 */
function SidebarContents({
  items,
  auth,
  isDark,
}: {
  items: NavItem[];
  auth: ReturnType<typeof useAuth>;
  isDark: boolean;
}) {
  return (
    <>
      <div className="px-4 py-5 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-md bg-gradient-to-br from-primary to-primary/60 grid place-items-center text-primary-foreground text-xs font-bold tracking-tight">
            W
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight leading-none">
              Wooly
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {auth.faction?.name ?? "—"}
            </div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 px-2.5 py-1.5 text-sm rounded-md transition-colors",
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground",
              )
            }
          >
            <item.icon className="size-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-sidebar-border">
        <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
          <div className="size-7 rounded-full bg-muted grid place-items-center text-xs font-medium">
            {auth.user?.name?.[0]?.toUpperCase() ?? "?"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">
              {auth.user?.name ?? "Signed out"}
            </div>
            {auth.isServiceAdmin ? (
              <div className="text-[10px] text-primary uppercase tracking-wide">
                Admin
              </div>
            ) : auth.isLeader ? (
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Leader
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start text-muted-foreground"
            onClick={() => {
              logout();
              window.location.hash = "#/";
            }}
          >
            <LogOut className="size-4" />
            Sign out
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground"
            aria-label="Toggle theme"
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            onClick={() => setTheme(isDark ? "light" : "dark")}
          >
            {isDark ? (
              <Sun className="size-4" />
            ) : (
              <Moon className="size-4" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Two-column app shell. The outer container is locked to viewport
 * height with overflow hidden so the sidebar stays put — only the
 * main column scrolls. This keeps the sign-out and section nav
 * always-visible regardless of how long the route content gets.
 *
 * On mobile (below md), the sidebar collapses behind a hamburger
 * button in a sticky top header; tapping the button slides in the
 * same sidebar contents as a drawer.
 */
export function Layout() {
  const auth = useAuth();
  const theme = useTheme();
  const adminFaction = useAdminFaction();
  const qc = useQueryClient();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  const items = NAV_ITEMS.filter((i) => !i.gate || i.gate(auth));

  // Auto-close the mobile drawer on navigation so tapping a link
  // doesn't leave the drawer covering the page.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <div className="h-screen flex flex-col md:flex-row overflow-hidden bg-background">
      {/* Mobile top header — hidden on md+ */}
      <header className="md:hidden flex items-center gap-2 border-b bg-sidebar px-3 py-2 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open navigation menu"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="size-5" />
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="size-6 rounded-md bg-gradient-to-br from-primary to-primary/60 grid place-items-center text-primary-foreground text-[10px] font-bold tracking-tight shrink-0">
            W
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold tracking-tight leading-none truncate">
              Wooly
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {auth.faction?.name ?? "—"}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay + slide-in — hidden on md+ */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="relative z-10 w-64 max-w-[80%] bg-sidebar border-r flex flex-col shadow-xl"
            role="dialog"
            aria-label="Navigation menu"
          >
            <SidebarContents items={items} auth={auth} isDark={isDark} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar — hidden below md */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r bg-sidebar">
        <SidebarContents items={items} auth={auth} isDark={isDark} />
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        {adminFaction.factionId !== null && auth.isServiceAdmin && (
          <div className="border-b bg-amber-100/60 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 px-4 md:px-8 py-2 flex items-center justify-between gap-3 text-sm">
            <span className="inline-flex items-center gap-2 min-w-0">
              <Eye className="size-4 shrink-0" />
              <span className="truncate">
                Viewing as{" "}
                <span className="font-semibold">
                  {adminFaction.factionName ?? `#${adminFaction.factionId}`}
                </span>{" "}
                <span className="text-amber-700/70 dark:text-amber-400/70 tabular-nums">
                  (#{adminFaction.factionId})
                </span>
              </span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 text-amber-900 dark:text-amber-200 hover:bg-amber-200/40 dark:hover:bg-amber-900/30"
              onClick={() => {
                clearAdminFactionOverride();
                // Faction-scoped queries are keyed on factionId so the
                // cache will refresh; invalidate to be doubly safe.
                qc.invalidateQueries();
              }}
            >
              <X className="size-3.5" />
              Stop
            </Button>
          </div>
        )}
        <div className="container mx-auto py-8 px-4 md:px-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
